'use client';

import { useCallback } from 'react';

import { apiGtm, buildBannerPromotion, useGTMViewPromotionCarousel } from '@/api/gtm';
import { PageBlock } from '@/payload/payload-types-block';
import { UiBannerCarouselMini } from '@/ui/banner-carousel-mini';
import { toAbsoluteUrl } from '@/util/to-absolute-url';

type BannerCarouselMini = PageBlock<'bannerCarouselMini'>;

interface FeatureBannerCarouselMiniProps {
  banners: BannerCarouselMini['slides'];
  titleLink: BannerCarouselMini['titleLink'];
  trackingName: string;
  countDesktopSlide?: BannerCarouselMini['countDesktopSlide'];
  onLinkClick?: (label?: string, skipMenuClick?: boolean) => void;
}

export function FeatureBannerCarouselMini({
  banners,
  titleLink,
  trackingName,
  countDesktopSlide,
  onLinkClick
}: FeatureBannerCarouselMiniProps) {
  const getPromotion = useCallback(
    (index: number) => {
      const banner = banners[index];
      if (!banner) return;

      return buildBannerPromotion({
        id: banner.id,
        name: banner.title ?? '',
        imageUrl: toAbsoluteUrl(
          typeof banner.image === 'number' ? '' : (banner.image?.url ?? '')
        ),
        linkUrl: banner.link?.url,
        trackingName,
        index
      });
    },
    [banners, trackingName]
  );

  const onCarouselInit = useGTMViewPromotionCarousel(getPromotion);

  const handleBannerClick = (index: number): boolean => {
    const promotion = getPromotion(index);
    if (!promotion) return false;

    apiGtm.selectPromotion({
      ecommerce: {
        promoClick: {
          promotions: [promotion]
        }
      }
    });
    return true;
  };

  return (
    <UiBannerCarouselMini
      banners={banners}
      titleLink={titleLink}
      countDesktopSlide={countDesktopSlide}
      onLinkClick={onLinkClick}
      onCarouselInit={onCarouselInit}
      onBannerClick={handleBannerClick}
    />
  );
}
